import { Group, Progress, Stack, Table, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { FC, Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { NgpRecapRow } from '../../data-access/types/gp-sampling-assignment';
import { SAMPLER_UNASSIGNED_COLOR } from '../gp-assignment-map/point-colors';

import {
  groupRecapByOrganization,
  recapPercent,
  sumRecapRows,
  type RecapFigures,
  type RecapOrganizationGroup,
} from './group-rows';

interface NgpRecapOrganizationTableProps {
  rows: NgpRecapRow[];
  search: string;
}

function laboratoryLabel(
  laboratory: { name: string | null },
  fallback: string,
): string {
  return laboratory.name ?? fallback;
}

const CountCell: FC<{ value: number }> = ({ value }) => (
  <Table.Td
    c={value === 0 ? 'dimmed' : undefined}
    fw={value === 0 ? 400 : 600}
    ta="right"
  >
    {value}
  </Table.Td>
);

const SampledCell: FC<{
  figures: Pick<RecapFigures, 'finished' | 'impossible' | 'pending' | 'points'>;
  sampledLabel: string;
  impossibleLabel: (count: number) => string;
  pendingLabel: (count: number) => string;
}> = ({ figures, impossibleLabel, pendingLabel, sampledLabel }) => (
  <Table.Td>
    <Stack gap={4}>
      <Group gap="xs" justify="space-between">
        <Text fw={600} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {figures.finished} / {figures.points}
        </Text>
        <Text c="dimmed" size="xs">
          {recapPercent(figures.finished, figures.points)}%
        </Text>
      </Group>
      <Progress
        aria-label={sampledLabel}
        color="green"
        size="sm"
        value={recapPercent(figures.finished, figures.points)}
      />
      {figures.pending > 0 || figures.impossible > 0 ? (
        <Group gap="sm">
          {figures.pending > 0 ? (
            <Text c="dimmed" size="xs">{pendingLabel(figures.pending)}</Text>
          ) : null}
          {figures.impossible > 0 ? (
            <Text c="red.7" size="xs">{impossibleLabel(figures.impossible)}</Text>
          ) : null}
        </Group>
      ) : null}
    </Stack>
  </Table.Td>
);

const LaboratoriesCell: FC<{
  figures: RecapFigures;
  noLaboratory: string;
}> = ({ figures, noLaboratory }) => (
  <Table.Td>
    <Group gap="md" wrap="wrap">
      {figures.laboratories.map((laboratory) => (
        <Group gap={6} key={laboratory.id ?? 'none'} wrap="nowrap">
          <Text c="dimmed" size="xs">
            {laboratoryLabel(laboratory, noLaboratory)}
          </Text>
          <Text fw={600} size="sm">{laboratory.points}</Text>
        </Group>
      ))}
    </Group>
  </Table.Td>
);

const MetricCells: FC<{
  figures: RecapFigures;
  impossibleLabel: (count: number) => string;
  noLaboratory: string;
  pendingLabel: (count: number) => string;
  sampledLabel: string;
}> = ({ figures, impossibleLabel, noLaboratory, pendingLabel, sampledLabel }) => (
  <>
    <Table.Td ta="right">
      <Text fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {figures.points}
      </Text>
    </Table.Td>
    <SampledCell
      figures={figures}
      impossibleLabel={impossibleLabel}
      pendingLabel={pendingLabel}
      sampledLabel={sampledLabel}
    />
    <CountCell value={figures.hidden} />
    <CountCell value={figures.sms} />
    <CountCell value={figures.organic} />
    <CountCell value={figures.blocked} />
    <LaboratoriesCell figures={figures} noLaboratory={noLaboratory} />
  </>
);

function samplerFigures(row: NgpRecapRow): RecapFigures {
  return {
    blocked: row.organic_blocked,
    finished: row.points_finished,
    hidden: row.points_total - row.points_visible,
    impossible: row.points_impossible,
    laboratories: row.laboratories,
    organic: row.organic_pending,
    pending: row.points_pending,
    points: row.points_total,
    sms: row.points_total - row.sms_sent,
  };
}

export const NgpRecapOrganizationTable: FC<NgpRecapOrganizationTableProps> = ({
  rows,
  search,
}) => {
  const { t } = useTranslation();
  const i18n = (key: string, options?: Record<string, unknown>) =>
    t(`ngpAssignment.recap.${key}`, options);

  const [override, setOverride] = useState<{
    choices: Record<string, boolean>;
    query: string;
  }>({ choices: {}, query: '' });
  const query = search.trim().toLowerCase();
  const choices = override.query === query ? override.choices : {};
  const unassignedLabel = i18n('unassignedSampler').toLowerCase();

  const groups = useMemo(
    () =>
      groupRecapByOrganization(rows, {
        noOrganization: t('ngpAssignment.recap.noOrganization'),
        unassignedSampler: t('ngpAssignment.recap.unassignedSampler'),
      }),
    [rows, t],
  );
  const totals = useMemo(() => sumRecapRows(rows), [rows]);

  const defaultExpanded = (group: RecapOrganizationGroup) => {
    if (!query) {
      return false;
    }
    return group.rows.some((row) =>
      (row.sampler_name ?? unassignedLabel).toLowerCase().includes(query),
    );
  };

  const isExpanded = (group: RecapOrganizationGroup) => {
    if (!group.expandable) {
      return false;
    }
    const choice = choices[group.id];
    if (choice != null) {
      return choice;
    }
    return defaultExpanded(group);
  };

  const toggle = (group: RecapOrganizationGroup) => {
    setOverride((current) => {
      const active = current.query === query ? current.choices : {};
      const choice = active[group.id];
      const open = choice != null ? choice : defaultExpanded(group);
      return { choices: { ...active, [group.id]: !open }, query };
    });
  };

  const metricLabels = {
    impossibleLabel: (count: number) => i18n('sampledImpossible', { count }),
    noLaboratory: i18n('noLaboratory'),
    pendingLabel: (count: number) => i18n('sampledPending', { count }),
    sampledLabel: i18n('columnSampled'),
  };

  return (
    <Table.ScrollContainer maxHeight="55vh" minWidth={1100}>
      <Table highlightOnHover stickyHeader verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{i18n('columnOrganization')}</Table.Th>
            <Table.Th ta="right">{i18n('metricPoints')}</Table.Th>
            <Table.Th style={{ width: 220 }}>{i18n('columnSampled')}</Table.Th>
            <Table.Th ta="right">{i18n('labelHidden')}</Table.Th>
            <Table.Th ta="right">{i18n('labelSms')}</Table.Th>
            <Table.Th ta="right">{i18n('labelOrganic')}</Table.Th>
            <Table.Th ta="right">{i18n('labelBlocked')}</Table.Th>
            <Table.Th>{i18n('laboratoriesTitle')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {groups.map((group) => {
            const open = isExpanded(group);
            const unassigned = group.id === 'unassigned';
            return (
              <Fragment key={group.id}>
                <Table.Tr
                  style={unassigned
                    ? { boxShadow: `inset 3px 0 0 ${SAMPLER_UNASSIGNED_COLOR}` }
                    : undefined}
                >
                  <Table.Td>
                    {group.expandable ? (
                      <UnstyledButton
                        aria-expanded={open}
                        onClick={() => toggle(group)}
                        style={{ alignItems: 'center', display: 'flex', gap: 8, textAlign: 'left' }}
                      >
                        {open ? (
                          <IconChevronDown aria-hidden size={16} />
                        ) : (
                          <IconChevronRight aria-hidden size={16} />
                        )}
                        <span>
                          <Text fw={600} size="sm">{group.label}</Text>
                          <Text c="dimmed" size="xs">
                            {i18n('samplerCount', { count: group.rows.length })}
                          </Text>
                        </span>
                      </UnstyledButton>
                    ) : (
                      <Text fw={600} pl={24} size="sm">{group.label}</Text>
                    )}
                  </Table.Td>
                  <MetricCells figures={group.figures} {...metricLabels} />
                </Table.Tr>
                {open
                  ? group.rows.map((row) => {
                    const rowUnassigned = row.sampler_id == null;
                    return (
                      <Table.Tr
                        key={`${group.id}-${row.sampler_id ?? 'unassigned'}`}
                        style={{
                          background: 'var(--mantine-color-gray-1)',
                          boxShadow: rowUnassigned
                            ? `inset 3px 0 0 ${SAMPLER_UNASSIGNED_COLOR}`
                            : undefined,
                        }}
                      >
                        <Table.Td>
                          <Text pl={40} size="sm">
                            {row.sampler_name ?? i18n('unassignedSampler')}
                          </Text>
                        </Table.Td>
                        <MetricCells figures={samplerFigures(row)} {...metricLabels} />
                      </Table.Tr>
                    );
                  })
                  : null}
              </Fragment>
            );
          })}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Th>{i18n('total')}</Table.Th>
            <Table.Th ta="right">{totals.points}</Table.Th>
            <Table.Th>
              {totals.finished} / {totals.points}
            </Table.Th>
            <Table.Th ta="right">{totals.hidden}</Table.Th>
            <Table.Th ta="right">{totals.sms}</Table.Th>
            <Table.Th ta="right">{totals.organic}</Table.Th>
            <Table.Th ta="right">{totals.blocked}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </Table.ScrollContainer>
  );
};
